<template>
  <loading v-if="loading"></loading>
  <div
    v-else
    class="history-view-component"
  >
    <h2>Past 7 Days</h2>
    <div class="seven-day-history">
      <seven-day-history-bar-chart
        :data="sevenDayGraphData"
        :options="graphOptions"
        :styles="{ height: '100%' }"
      ></seven-day-history-bar-chart>
    </div>
    <h2>Your Recent Activity</h2>
    <div class="asset-type-tabs">
      <button
        v-for="assetType in tabs"
        :key="assetType"
        class="asset-type-tab-button"
        :class="{ active: activeAssetType === assetType }"
        @click="setActiveAssetType(assetType)"
      >
        {{ assetType }} ({{ counts[assetType] }})
      </button>
    </div>
    <div class="list-container asset-list">
      <div class="list-heading row asset-type-header">
        <icon
          class="asset-type-icon"
          :name="headerIcon"
        />
        {{ activeAssetType }}
      </div>
      <component
        :is="asset.listRowType"
        v-for="asset in displayRows"
        :key="`${asset.assetType}-${asset.id}`"
        :attachment="asset"
        :show-lock="false"
        :topic="asset"
        @attachment-clicked="handleRowClick(asset)"
        @row-clicked="handleRowClick(asset)"
      />
    </div>
  </div>
</template>

<script>
import differenceInDays from 'date-fns/differenceInDays';
import subDays from 'date-fns/subDays';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';
import AttachmentListRow from '@/components/attachments/listRow.vue';
import TopicListRow from '@/components/topics/listRow.vue';
import Icon from '@/components/singletons/icon.vue';
import Loading from '@/components/singletons/loading.vue';

import SevenDayHistoryBarChart from './seven-day-history-bar-chart';

const ASSET_TYPES = {
  ALL: 'All',
  ARTICLES: 'Articles',
  AUDIO: 'Audio',
  HUMANKIT: '3D Anatomy',
  IMAGES: 'Images',
  VIDEOS: 'Video',
};

export default {
  name: 'HistoryView',
  components: {
    AttachmentListRow,
    Icon,
    Loading,
    SevenDayHistoryBarChart,
    TopicListRow,
  },
  data() {
    return {
      activeAssetType: ASSET_TYPES.ALL,
      loading: false,
      tabs: [
        ASSET_TYPES.ALL,
        ASSET_TYPES.VIDEOS,
        ASSET_TYPES.AUDIO,
        ASSET_TYPES.ARTICLES,
        ASSET_TYPES.HUMANKIT,
        ASSET_TYPES.IMAGES,
      ],
    };
  },
  computed: {
    attachments() {
      const attachmentIds = this.attachmentStates.map(
        (aState) => aState.attachmentId,
      );
      const attachments = this.$store.getters['attachments/get'];
      const displayAttachments = [];
      (attachmentIds || []).forEach((id) =>
        displayAttachments.push(attachments[id]),
      );
      return displayAttachments;
    },
    attachmentStates() {
      const today = new Date();
      return (this.$store.getters['attachmentStates/list'] || []).filter(
        (aState) => {
          return differenceInDays(today, aState.mobileCreatedAt) <= 7;
        },
      );
    },
    bookmarkStates() {
      return this.$store.getters['bookmarks/bookmarkList'];
    },
    counts() {
      const counts = {
        [ASSET_TYPES.ARTICLES]: this[ASSET_TYPES.ARTICLES].length,
        [ASSET_TYPES.AUDIO]: this[ASSET_TYPES.AUDIO].length,
        [ASSET_TYPES.HUMANKIT]: this[ASSET_TYPES.HUMANKIT].length,
        [ASSET_TYPES.IMAGES]: this[ASSET_TYPES.IMAGES].length,
        [ASSET_TYPES.VIDEOS]: this[ASSET_TYPES.VIDEOS].length,
      };
      counts[ASSET_TYPES.ALL] = Object.keys(counts).reduce(
        (acc, curr) => (acc += counts[curr]),
        0,
      );

      return counts;
    },
    displayRows() {
      return this[this.activeAssetType];
    },
    graphOptions() {
      return {
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                suggestedMax: 10,
              },
            },
          ],
        },
      };
    },
    headerIcon() {
      if (this.activeAssetType === ASSET_TYPES.ARTICLES) {
        return 'topics';
      } else if (this.activeAssetType === ASSET_TYPES.VIDEOS) {
        return 'video-camera';
      } else if (this.activeAssetType === ASSET_TYPES.AUDIO) {
        return 'speaker';
      }
    },
    sevenDayGraphData() {
      const colors = ['yellow', 'red', 'green', 'blue', 'purple'];
      const sevenDays = new Array(7)
        .fill(new Date())
        .map((date, index) => subDays(date, 6 - index));

      const datasets = this.tabs
        .filter((tab) => tab !== ASSET_TYPES.ALL)
        .map((tab, index) => {
          const data = sevenDays.map((day) => {
            return this[tab].filter((asset) => {
              let state;
              if (asset?.assetType === 'Attachment') {
                state = this.attachmentStates.find(
                  (state) => state.attachmentId === asset.id,
                );
              } else {
                state = this.topicStates.find(
                  (state) => state.topicId === asset.id,
                );
              }

              return isSameDay(day, new Date(state.mobileCreatedAt));
            }).length;
          });

          return {
            label: tab,
            backgroundColor: colors[index],
            data,
          };
        });

      return {
        labels: sevenDays.map((date) => format(date, 'LL/dd/yy')),
        datasets,
      };
    },
    topics() {
      const topicIds = this.topicStates.map((tState) => tState.topicId);
      const topics = this.$store.getters['topics/topics'];
      const displayTopics = [];
      (topicIds || []).forEach((id) => displayTopics.push(topics[id]));
      return displayTopics;
    },
    topicStates() {
      const today = new Date();
      return (this.$store.getters['topicStates/all'] || []).filter((tState) => {
        return differenceInDays(today, tState.mobileCreatedAt) <= 7;
      });
    },
    [ASSET_TYPES.ALL]() {
      const assets = [...this.attachments, ...this.topics];
      return this.sortList(assets);
    },
    [ASSET_TYPES.ARTICLES]() {
      return this.sortList(this.topics);
    },
    [ASSET_TYPES.AUDIO]() {
      return this.sortList(
        this.attachments.filter((a) => a.mediaType === 'audio'),
      );
    },
    [ASSET_TYPES.HUMANKIT]() {
      return this.sortList(
        this.attachments.filter((a) => a.mediaType === 'biodigital'),
      );
    },
    [ASSET_TYPES.IMAGES]() {
      return this.sortList(
        this.attachments.filter((a) => a.mediaType === 'image'),
      );
    },
    [ASSET_TYPES.VIDEOS]() {
      return this.sortList(
        this.attachments.filter((a) => a.mediaType === 'video'),
      );
    },
  },
  async created() {
    this.loading = true;
    const { data } = await this.$store.dispatch('flashcardStates/fetchStates', [
      'bookmarks',
      'attachments',
      'topics',
    ]);
    if (data?.topics?.length > 0) {
      await this.$store.dispatch('topics/load', {
        ids: data.topics.map((state) => state.topicId),
      });
    }
    this.loading = false;
  },
  methods: {
    handleRowClick(asset) {
      this.$router.push({
        name: 'library#show',
        params: {
          assetType: asset.assetType.toLowerCase(),
          assetId: asset.id,
        },
      });
    },
    setActiveAssetType(assetType) {
      this.activeAssetType = assetType;
    },
    sortList(arr) {
      return [...arr].sort((a, b) => {
        let aState, bState;
        if (a?.assetType === 'Attachment') {
          aState = this.attachmentStates.find(
            (state) => state.attachmentId === a.id,
          );
        } else {
          aState = this.topicStates.find((state) => state.topicId === a.id);
        }
        if (b?.assetType === 'Attachment') {
          bState = this.attachmentStates.find(
            (state) => state.attachmentId === b.id,
          );
        } else {
          bState = this.topicStates.find((state) => state.topicId === b.id);
        }

        return aState.mobileCreatedAt < bState.mobileCreatedAt ? 1 : -1;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.history-view-component {
  background: #edf1f5;
  @include full-padding;

  .seven-day-history {
    @include shadowed;
    position: relative;
    background-color: #ffffff;
    border-radius: 8px;
    box-sizing: border-box;
    height: 300px;
    padding: 32px;
    width: 100%;
  }

  .asset-type-tabs {
    .asset-type-tab-button {
      background: transparent;
      border-bottom: 1px solid #333333;
      color: #333333;
      font-size: rem-calc(12);
      margin-left: rem-calc(24);
      padding: rem-calc(4);

      &.active {
        border-color: #00dbfb;
        color: #000000;
        font-weight: $bold;
      }

      &:first-child {
        margin-left: 0;
      }
    }
  }

  .asset-list {
    margin: rem-calc(32) 0;

    .asset-type-header {
      color: #000000;
      text-transform: uppercase;

      .asset-type-icon {
        height: rem-calc(16);
        margin: 0 rem-calc(12) 0 rem-calc(4);
        width: rem-calc(16);
      }
    }
  }
}
</style>
